import { Directive, ElementRef, EventEmitter, HostListener, Output } from '@angular/core';

/**
 * Directive para detectar gestos de swipe horizontal usando touch events nativos
 *
 * Eventos emitidos:
 * - swipeLeft: Swipe para a esquerda (próxima aba)
 * - swipeRight: Swipe para a direita (aba anterior)
 *
 * Uso:
 * <div (swipeLeft)="nextPage()" (swipeRight)="previousPage()" swipeGesture>
 *   Conteúdo aqui
 * </div>
 */
@Directive({
  standalone: true,
  selector: '[swipeGesture]'
})
export class SwipeGestureDirective {

  @Output() swipeLeft = new EventEmitter<void>();
  @Output() swipeRight = new EventEmitter<void>();

  private touchStartX = 0;
  private touchStartY = 0;
  private touchEndX = 0;
  private touchEndY = 0;

  // Configurações do gesto
  private readonly minSwipeDistance = 50; // Distância mínima para considerar um swipe
  private readonly maxVerticalDistance = 100; // Máxima distância vertical permitida

  constructor(private elementRef: ElementRef) {
    // Garantir que o elemento capture todos os eventos de touch
    this.setupElement();
  }

  private setupElement(): void {
    const element = this.elementRef.nativeElement;

    // Garantir que o elemento tenha altura mínima para capturar touches
    if (element.style.minHeight === '') {
      element.style.minHeight = '100%';
    }

    // Adicionar position relative se não tiver
    const computedStyle = window.getComputedStyle(element);
    if (computedStyle.position === 'static') {
      element.style.position = 'relative';
    }
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    if (event.touches.length !== 1) return; // Apenas um dedo

    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;

    // Debug: log da posição inicial
    console.log('Touch start:', { x: this.touchStartX, y: this.touchStartY });
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    if (event.changedTouches.length !== 1) return; // Apenas um dedo

    const touch = event.changedTouches[0];
    this.touchEndX = touch.clientX;
    this.touchEndY = touch.clientY;

    // Debug: log da posição final
    console.log('Touch end:', { x: this.touchEndX, y: this.touchEndY });

    this.handleSwipe();
  }

  private handleSwipe(): void {
    const horizontalDistance = this.touchEndX - this.touchStartX;
    const verticalDistance = Math.abs(this.touchEndY - this.touchStartY);

    console.log('Swipe analysis:', {
      horizontalDistance,
      verticalDistance,
      minDistance: this.minSwipeDistance,
      maxVertical: this.maxVerticalDistance
    });

    // Verificar se é um movimento horizontal válido
    if (Math.abs(horizontalDistance) < this.minSwipeDistance) {
      console.log('Swipe rejected: too small');
      return; // Movimento muito pequeno
    }

    // Verificar se não é movimento muito vertical (scroll)
    if (verticalDistance > this.maxVerticalDistance) {
      console.log('Swipe rejected: too vertical');
      return; // Movimento muito vertical, provavelmente scroll
    }

    // Verificar se é swipe horizontal predominante
    if (Math.abs(horizontalDistance) < verticalDistance) {
      console.log('Swipe rejected: more vertical than horizontal');
      return; // Movimento mais vertical que horizontal
    }

    // Adicionar classe de transição temporariamente
    const element = this.elementRef.nativeElement;
    const tabContent = element.querySelector('.tab-transition');

    if (tabContent) {
      tabContent.classList.add('changing');
      setTimeout(() => {
        tabContent.classList.remove('changing');
      }, 300);
    }

    if (horizontalDistance > 0) {
      console.log('Swipe RIGHT detected');
      this.swipeRight.emit();
    } else {
      console.log('Swipe LEFT detected');
      this.swipeLeft.emit();
    }
  }

  // Controlar scroll durante o swipe
  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    const currentX = touch.clientX;
    const currentY = touch.clientY;

    const deltaX = Math.abs(currentX - this.touchStartX);
    const deltaY = Math.abs(currentY - this.touchStartY);

    if (deltaX > deltaY && deltaX > 10) {
      event.preventDefault();
    }
  }
}
